import {AxiosError} from "axios";
import {TFunction} from "i18next";
import {NavigateFunction} from "react-router/dist/lib/hooks";
import {logger} from "./Logger";
import {StringUtils} from "./StringUtils";

/**
 * Utility function to block the main thread for some time.
 *
 * NOTE: THE MAIN PURPOSE FOR THIS IS TO ARTIFICIALLY INTRODUCE SOME DELAY FOR TESTING PURPOSES DURING DEVELOPMENT
 */
function blockMainThreadWhileBurningYourCPU(ms : number)
{
    logger.warn(`BURNING CPU FOR ${ms} MILLISECONDS`);

    const start = performance.now();

    while (performance.now() - start < ms);
}

function redirectToLoginIfNotAuthenticated (err : Error, navigate : NavigateFunction) {
    if (err instanceof AxiosError && err.response?.status === 401)
    {
        if (err.response?.headers["www-authenticate"] === "TOTP") {
            navigate("/tfa");
        }
        else if (err.response?.headers["www-authenticate"] === "TOTP-SETUP-REQUIRED") {
            // Authenticated but 2FA setup not yet complete — navigate to root so the
            // TfaSetupRequired guard can show the enrollment wizard.
            navigate("/");
        }
        else {
            navigate("/login");
        }
    }
}

/**
 * Returns the object the parameters of a back-end error are interpolated from: the fields of the first
 * parameter, so the {{p.value}} most keys use keeps working, and every parameter by its name, for a key
 * with several parameters ({{p.subject}}, {{p.reason}}).
 */
function toTranslationParams(params : {name?: string, value?: string}[]) : Record<string, unknown>
{
    const translationParams : Record<string, unknown> = {...params[0]};

    for (const param of params)
    {
        if (param?.name !== undefined) {
            translationParams[param.name] = param.value;
        }
    }

    return translationParams;
}

function getErrorMessage(err : Error, t : TFunction<string, undefined> | undefined) : string
{
    let errorMessage : string | undefined = undefined;

    if (err instanceof AxiosError)
    {
        let data = err.response?.data;

        if (data !== undefined && data !== null)
        {
            errorMessage = data["message"]

            // the error message can contain a nested JSON message when the error is created by the portal back-end
            if (errorMessage !== undefined && errorMessage !== null)
            {
                try {
                    const nestedError = JSON.parse(errorMessage);

                    if (nestedError["className"] === "com.ciphermail.core.app.properties.PropertyValidationFailedException") {
                        data = nestedError;
                    }
                }
                catch (e) {
                    // ignore
                }
            }

            if (data["className"] === "com.ciphermail.core.app.properties.PropertyValidationFailedException")
            {
                if (t !== undefined) {
                    // if there are parameters, use them for the translation, otherwise use the key only
                    const translatedErrorMessage = Array.isArray(data["params"]) && data["params"].length > 0 ?
                        t(data["key"], {'p': toTranslationParams(data["params"])}) :
                        t(data["key"]);

                    if (translatedErrorMessage !== null && translatedErrorMessage !== undefined &&
                        !StringUtils.isBlank(translatedErrorMessage))
                    {
                        errorMessage = translatedErrorMessage;
                    }
                    else if (errorMessage !== undefined) {
                        try {
                            const nestedError = JSON.parse(errorMessage);

                            errorMessage = nestedError["message"];
                        }
                        catch (e) {
                            // ignore
                        }
                    }
                }
            }
        }
    }

    if (StringUtils.isBlank(errorMessage) || errorMessage === undefined || errorMessage === null) {
        errorMessage = err.message;
    }

    if (StringUtils.isBlank(errorMessage)) {
        errorMessage = "Unknown error"
    }

    return errorMessage;
}

/**
 * Converts a number-like value to an integer string representation.
 *
 * @param {string | undefined | null} value - The value to convert.
 *
 * @return {string | undefined | null} - The integer string representation of the value.
 */
function numberToIntString(value : string)
{
    return value !== undefined && value !== null && value.length > 0 ? Math.floor(Number(value)).toString() : value;
}

/**
 * Converts a string value to the corresponding enum value.
 *
 * @param {object} enumObject - The enum object.
 * @param {string} value - The string value to convert.
 * @return {string} - The converted enum value.
 * @throws {Error} - If the provided value is not found in the enum.
 */
function stringToEnum<T extends {[key: string]: string}, K extends keyof T & string>(enumObject: T, value: `${T[K]}`): T[K]
{
    if (Object.values(enumObject).includes(value)) {
        return (value as unknown) as T[K];
    } else {
        throw new Error('Value provided was not found in Enum');
    }
}

/**
 * Retrieves the keys of an enum object.
 *
 * @param {Object} enumObject - The enum object.
 * @return {string[]} - An array containing the keys of the enum object.
 */
function getEnumKeys<T extends {[key: string]: string}>(enumObject: T): string[] {
    return Object.keys(enumObject);
}

export type ByteUnit = 'Bytes' | 'KB' | 'MB' | 'GB' | 'TB' | 'PB' | 'EB' | 'ZB' | 'YB';

// one decimal, because a whole unit is too coarse for a size: a 1.6MB attachment reads "2 MB" without it,
// and a mailbox which is 70% full reads "1 GB/2 GB". It costs nothing for a round value, because the
// parseFloat below drops the trailing zero, so 2000000 is "2 MB" and not "2.0 MB". A byte count is a whole
// number, so at the Bytes unit the decimal never shows either
const DEFAULT_DECIMALS = 1

function formatBytes(numberOfBytes : number | undefined | null, decimals = DEFAULT_DECIMALS, inputUnit : ByteUnit = 'Bytes', outputUnit? : ByteUnit) : string
{
    // nothing to show rather than a size of nothing, so no unit is claimed for a value which is not known
    if (numberOfBytes === undefined || numberOfBytes === null) {
        return "0";
    }

    // the only value which leaves before a unit is appended below, so it gets one here. A caller which
    // asked for a unit supplies the label itself, which is why that case stays a bare number
    if (numberOfBytes === 0) {
        return outputUnit ? "0" : "0 Bytes";
    }

    // 1000 and not 1024: KB, MB and the rest are decimal, which is what a mail server, a quota and a
    // disk are all measured in
    const k = 1000
    const dm = Math.max(decimals, 0)
    const sizes : ByteUnit[] = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    // the sign is kept apart and the unit is chosen from the size, because the unit comes from a logarithm
    // and the logarithm of a negative number is NaN: a caller which formats the difference between two
    // sizes would otherwise read "NaN undefined". What such a difference means is for the caller to say,
    // so it is shown as what it is rather than clamped away here
    const negative = numberOfBytes < 0
    const bytes = Math.abs(numberOfBytes) * Math.pow(k, sizes.indexOf(inputUnit))
    const i = outputUnit ? sizes.indexOf(outputUnit) : Math.floor(Math.log(bytes) / Math.log(k))

    return `${negative ? "-" : ""}${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${
        outputUnit ? "" : " " + sizes[i]}`
}

function percentageFormatter(num : number | undefined | null) : string
{
    if (num === undefined || num === null) {
        return "";
    }

    return new Intl.NumberFormat('default', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num / 100);
}

function createPublicHostResourceUrl(id : string){
    return "/api/hostresources/getPublicResource?id=" + id + "&hostname=" + window.location.hostname;
}

function clickOnEnterKeyPressed(event : React.KeyboardEvent<HTMLElement>, clickFunction? : () => void)
{
    if (event.key === 'Enter')
    {
        event.preventDefault();

        if (clickFunction !== undefined) {
            clickFunction()
        }
        else {
            (event.target as HTMLElement).click()
        }
    }
}

export const Utils = () => {
    return
}

Utils.blockMainThreadWhileBurningYourCPU = blockMainThreadWhileBurningYourCPU;
Utils.redirectToLoginIfNotAuthenticated = redirectToLoginIfNotAuthenticated;
Utils.getErrorMessage = getErrorMessage;
Utils.numberToIntString = numberToIntString;
Utils.stringToEnum = stringToEnum;
Utils.getEnumKeys = getEnumKeys;
Utils.formatBytes = formatBytes;
Utils.percentageFormatter = percentageFormatter;
Utils.createPublicHostResourceUrl = createPublicHostResourceUrl;
Utils.clickOnEnterKeyPressed = clickOnEnterKeyPressed;